import { createFileRoute, redirect, useRouter } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { ClipLoader } from "react-spinners"
import { toast } from "sonner"
import { z } from "zod"

import { sendOtp } from "@/actions/auth-actions"
import {
  getTerminalAuthContext,
  isTerminalAuthorized,
  verifyTerminalOtp,
} from "@/actions/terminal-auth-actions"
import { terminalControlVariants } from "@/components/terminal/control-variants"
import { terminalFieldClassName } from "@/components/terminal/field"
import { ScreenGrain } from "@/components/terminal/screen-grain"
import { TerminalLogo } from "@/components/terminal/terminal-logo"
import { ThemeToggle } from "@/components/terminal/theme-toggle"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { getTerminalHeadMeta } from "@/lib/terminal/metadata"
import { useTerminalTheme } from "@/lib/terminal/use-terminal-theme"
import { cn, formatPhoneNumber } from "@/lib/utils"

import terminalLoginStyles from "./terminal-login.css?url"

const searchSchema = z.object({
  error: z.enum(["invalid_code", "unauthorized"]).optional(),
})

export const Route = createFileRoute("/terminal/login")({
  head: () => ({
    meta: getTerminalHeadMeta(),
    links: [
      { rel: "stylesheet", href: terminalLoginStyles },
      // The wordmark's Inter Display 600 at the lockup's size, subset to
      // its own letters.
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:opsz,wght@32,600&text=Night%20Shift&display=swap",
      },
    ],
  }),
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: async () => {
    const authContext = await getTerminalAuthContext()
    if (authContext.user && isTerminalAuthorized(authContext.userType)) {
      throw redirect({
        to: "/terminal",
        search: {},
      })
    }
  },
  component: TerminalLoginPage,
})

const ERROR_MESSAGES: Record<string, string> = {
  invalid_code:
    "Magic link expired or opened in wrong browser. Please open the link in the same browser where you requested it.",
  unauthorized: "You are not authorized to access Night Shift.",
}

function TerminalLoginPage() {
  const { error } = Route.useSearch()
  const { theme } = useTerminalTheme()
  // Set once the code verifies: the screen widens into the shell's main
  // screen and the lockup steps aside, then the route changes beneath it.
  const [isEntering, setIsEntering] = useState(false)

  useEffect(() => {
    if (error && ERROR_MESSAGES[error]) {
      toast.error(ERROR_MESSAGES[error])
    }
  }, [error])

  return (
    <div
      data-terminal-theme={theme}
      className={cn(
        "terminal-login-page",
        theme === "dark" && "dark",
        isEntering && "terminal-login-page--entering"
      )}
    >
      <div className="terminal-login-theme-toggle">
        <ThemeToggle />
      </div>
      {/* Sign-in is the product's own frame (Dhanur, 25 Oct): the lockup as
          one block on the chrome, the phone flow in a grained screen beside
          it. At phone width the two stack, lockup over screen. */}
      <main className="terminal-login-layout">
        <header className="terminal-login-brand">
          {/* The header's animated mark, larger. */}
          <TerminalLogo
            theme={theme}
            isDecorative
            className="terminal-login-showcase"
          />
          <div className="terminal-login-lockup">
            <h1 id="terminal-login-brand">Night Shift</h1>
            <p>KYB that never sleeps</p>
          </div>
        </header>
        <section
          className="terminal-login-access"
          aria-labelledby="terminal-login-brand"
        >
          <div className="terminal-login-content">
            <SmsLoginForm onEnteringChange={setIsEntering} />
          </div>
          <ScreenGrain />
        </section>
      </main>
    </div>
  )
}

// Matches the [auth.sms.test_otp] entry in supabase/config.toml, which lets
// local development bypass real Twilio SMS.
const LOCAL_TEST_PHONE = "(805) 450-9609"
const LOCAL_TEST_OTP = "123456"

// How long the screen takes to become the shell's; the route changes once
// it has. Under reduced motion there is no wait.
const ENTER_MS = 520

function SmsLoginForm({
  onEnteringChange,
}: {
  onEnteringChange: (isEntering: boolean) => void
}) {
  const [phoneNumber, setPhoneNumber] = useState("")
  const [otp, setOtp] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [shouldShowOtpInput, setShowOtpInput] = useState(false)
  const [isLocalhost, setIsLocalhost] = useState(false)
  const router = useRouter()

  useEffect(() => {
    const { hostname } = window.location
    setIsLocalhost(hostname === "localhost" || hostname === "127.0.0.1")
  }, [])

  async function handleSmsAuth() {
    try {
      setIsLoading(true)
      if (shouldShowOtpInput) {
        const result = await verifyTerminalOtp({
          data: { phone: phoneNumber, token: otp },
        })
        if (result.hasError) throw new Error(result.message)
        onEnteringChange(true)
        const isMotionReduced = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches
        // Invalidating re-runs this route's guard, which redirects at once,
        // so the screen gets its moment first.
        await new Promise((resolve) =>
          setTimeout(resolve, isMotionReduced ? 0 : ENTER_MS)
        )
        await router.invalidate()
        router.navigate({
          to: result.redirectTo,
          search: {},
        })
      } else {
        const result = await sendOtp({ data: { phone: phoneNumber } })
        if (result.hasError) throw new Error(result.message)
        setShowOtpInput(true)
        toast.success("Verification code sent!")
      }
    } catch (error: unknown) {
      // A failure after the screen began entering gives the form back.
      onEnteringChange(false)
      console.error("Error with SMS authentication:", error)
      toast.error(
        error instanceof Error ? error.message : "Failed to authenticate"
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className="terminal-login-form">
        <div className="terminal-login-field">
          <Label htmlFor="phone" className="type-micro text-muted-foreground">
            Phone number
          </Label>
          <div className="terminal-login-phone-control">
            <span aria-hidden="true">+1</span>
            <Input
              id="phone"
              placeholder="(415) 555-0123"
              type="tel"
              value={formatPhoneNumber(phoneNumber)}
              onChange={(event) => {
                const cleaned = event.target.value.replace(/\D/g, "")
                if (cleaned.length <= 10) setPhoneNumber(cleaned)
              }}
              disabled={isLoading || shouldShowOtpInput}
              className={cn(terminalFieldClassName("form"), "pl-12")}
            />
          </div>
        </div>
        {shouldShowOtpInput && (
          <div className="terminal-login-field">
            <Label htmlFor="otp" className="type-micro text-muted-foreground">
              Verification code
            </Label>
            <Input
              id="otp"
              placeholder="Enter verification code"
              value={otp}
              onChange={(event) => setOtp(event.target.value)}
              disabled={isLoading}
              className={terminalFieldClassName("form")}
            />
          </div>
        )}
        <Button
          onClick={handleSmsAuth}
          disabled={isLoading || !phoneNumber || (shouldShowOtpInput && !otp)}
          className={cn(
            terminalControlVariants({ size: "form", intent: "primary" }),
            "terminal-login-primary"
          )}
        >
          {isLoading && <ClipLoader color="#000" size={16} />}
          {shouldShowOtpInput ? "Verify code" : "Send code"}
        </Button>
      </div>
      {isLocalhost && (
        <p className="terminal-login-local-hint">
          Localhost: real SMS is bypassed. Sign in with phone{" "}
          <span>{LOCAL_TEST_PHONE}</span> and code <span>{LOCAL_TEST_OTP}</span>
          .
        </p>
      )}
    </>
  )
}
